import React from 'react';
import styled, { css } from 'styled-components';
import Truncate from 'react-truncate';
import TinyColor from 'tinycolor2';

import {
  GqlProductCardDisplayOptions,
  MobileAppFlair,
  ProductCardButtonOption,
  ProductCardTextAlignmentOption,
} from 'types/graphql';

import Imgix from 'shared/components/imgix';
import { ImgixDefaultSettings } from 'shared/constants';
import { formatCurrency } from 'shared/helpers/utils';
import { isWeightedProduct } from 'shared/helpers/products';

import { StarCheck } from 'src/svg/star-check';
import SvgAddIcon from 'src/dispensary-admin/customize/components/preview/home-page/carousels/product-carousel/product-carousel-card/svg/add-icon';

import { useGetMenuCustomization } from 'src/dispensary-admin/customize/data-access';
import { useProductCard } from './use-product-card';
import { Product } from './product-card.types';
import { SpecialOfferRotator } from './special-offer-rotator';

type ProductCardProps = {
  product: Product;
  isFullPreview?: boolean;
  isFullHeight?: boolean;
  displayOptions?: GqlProductCardDisplayOptions | null;
  asMobileApp?: boolean;
  buttonFlair?: MobileAppFlair;
};

export function getButtonClipPath(flair: MobileAppFlair | undefined): string {
  switch (flair) {
    case MobileAppFlair.angled:
      return 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)';
    case MobileAppFlair.jagged:
      return 'polygon(50% 0%, 60% 13%, 75% 7%, 77% 23%, 93% 25%, 87% 40%, 100% 50%, 87% 60%, 93% 75%, 77% 77%, 75% 93%, 60% 87%, 50% 100%, 40% 87%, 25% 93%, 23% 77%, 7% 75%, 13% 60%, 0% 50%, 13% 40%, 7% 25%, 23% 23%, 25% 7%, 40% 13%)';
    case MobileAppFlair.wavy:
      return 'polygon(96% 50%, 99% 56%, 98% 63%, 92% 68%, 87% 71%, 84% 76%, 83% 83%, 80% 89%, 75% 93%, 68% 92%, 61% 91%, 56% 92%, 50% 96%, 44% 99%, 37% 98%, 32% 92%, 29% 87%, 24% 84%, 17% 83%, 11% 80%, 7% 75%, 8% 68%, 9% 61%, 8% 56%, 4% 50%, 1% 44%, 2% 37%, 8% 32%, 13% 29%, 16% 24%, 17% 17%, 20% 11%, 25% 7%, 32% 8%, 39% 9%, 44% 8%, 50% 4%, 56% 1%, 63% 2%, 68% 8%, 71% 13%, 76% 16%, 83% 17%, 89% 20%, 93% 25%, 92% 32%, 91% 39%, 92% 44%)';
    case MobileAppFlair.curved:
    case MobileAppFlair.none:
    case undefined:
      return '';
    default: {
      const _exhaustiveCheck: never = flair;
      return _exhaustiveCheck;
    }
  }
}

export function isCircularButtonFlair(flair: MobileAppFlair | undefined): boolean {
  return !flair || flair === MobileAppFlair.none || flair === MobileAppFlair.curved;
}

const imgixParams = {
  ...ImgixDefaultSettings,
  h: 188,
  w: 188,
  fit: 'max',
};

export const ProductCard = ({
  product,
  isFullPreview = true,
  isFullHeight = true,
  displayOptions,
  asMobileApp = false,
  buttonFlair,
}: ProductCardProps): JSX.Element => {
  const { name, imageUrl, prices, brandName, strainType, potency } = useProductCard(product);

  const isWeighted = isWeightedProduct(product);
  const weight = product.Options?.[0];
  const { data } = useGetMenuCustomization();
  const { showProductWeight = false, textAlignment = ProductCardTextAlignmentOption.left } = data?.productCards ?? {};
  const showCollection = displayOptions?.showCollection ?? true;
  const showStaffPick = displayOptions?.showStaffPick ?? true;
  const showDiscountNames = displayOptions?.showDiscountNames ?? true;
  const showStrain = (displayOptions?.showStrainType ?? true) && !!strainType;
  const showEffects = (displayOptions?.showEffects ?? true) && !!product.effects?.length;
  const showPotency = (displayOptions?.showPotencyDetails ?? true) && potency.length > 0;
  const showWeight =
    isWeighted && (displayOptions?.showProductWeight ?? showProductWeight) && weight && weight !== 'N/A';

  return (
    <Wrapper $isFullHeight={isFullHeight} $isCentered={textAlignment === ProductCardTextAlignmentOption.center}>
      <div>
        <ImageWrapper>
          <Image src={imageUrl} imgixParams={imgixParams} />

          {showStaffPick && (
            <StaffPick>
              <StarCheck />
              Staff Pick
            </StaffPick>
          )}
        </ImageWrapper>

        <Name>
          <Truncate lines={2}>{name}</Truncate>
        </Name>

        {brandName && (
          <Brand>
            <Truncate>{brandName}</Truncate>
          </Brand>
        )}
      </div>
      <div>
        {(showStrain || showEffects || showPotency) && (
          <Details>
            {showStrain && <Strain>{strainType}</Strain>}
            {showPotency && <Potency $isMissingStrain={!showStrain && !showEffects}>{potency.join(' · ')}</Potency>}
            {showEffects && (
              <Effects>
                <Truncate>{product.effects?.join(' · ')}</Truncate>
              </Effects>
            )}
          </Details>
        )}

        {showDiscountNames && (
          <Tags>
            <SpecialOffer>
              <SpecialOfferRotator
                specialNames={product.specialNames ?? []}
                hideSalesLanguage={product.hideSalesLanguage ?? false}
              />
            </SpecialOffer>
          </Tags>
        )}

        {showCollection && product.type && <CollectionBadge color={null} isExclusive={false} title={product.type} />}

        {asMobileApp ? (
          <PriceRow>
            <div>
              <ActivePrice>{formatCurrency(prices?.[0] ?? 0)}</ActivePrice>
              {showWeight && <WeightText>{weight}</WeightText>}
            </div>
            <IconButton type='button' $buttonFlair={buttonFlair}>
              <SvgAddIcon />
            </IconButton>
          </PriceRow>
        ) : (
          <>
            <Price>
              {isFullPreview ? (
                <>
                  <ActivePrice>
                    {formatCurrency(prices?.[0] ?? 0)}

                    {showWeight && <Weight> - {weight}</Weight>}
                  </ActivePrice>

                  <DiscountedPrice>
                    <OriginalPrice>{formatCurrency(prices?.[0] ?? 0)}</OriginalPrice>
                    <Discount>% off</Discount>
                  </DiscountedPrice>
                </>
              ) : (
                <ActivePrice>
                  {formatCurrency(prices?.[0] ?? 0)}
                  {showWeight && <Weight> - {weight}</Weight>}
                </ActivePrice>
              )}
            </Price>

            <Button type='button'>Add to cart</Button>
          </>
        )}
      </div>
    </Wrapper>
  );
};

const Wrapper = styled.div<{ $isFullHeight: boolean; $isCentered: boolean }>`
  --text-alignment: ${({ $isCentered }) => ($isCentered ? 'center' : 'left')};
  --justify-content: ${({ $isCentered }) => ($isCentered ? 'center' : 'flex-start')};

  background: #fff;
  border: 1px solid #e3e7e9;
  border-radius: ${({ theme }) => theme?.radius?.lg};
  display: flex;
  flex-direction: column;
  height: ${({ $isFullHeight }) => ($isFullHeight ? '100%' : 'auto')};
  justify-content: space-between;
  padding: 16px;
  position: relative;
  text-align: var(--text-alignment);
  transition: all 0.2s;
  width: 100%;

  &:hover {
    border-color: #d2d7da;
    box-shadow: 0 4px 10px 0 #0000000d;
  }
`;

const ImageWrapper = styled.div`
  align-items: center;
  aspect-ratio: 1 / 1;
  display: flex;
  margin-bottom: 8px;
  position: relative;
  width: 100%;

  img {
    display: block;
    margin: 0 auto;
    max-height: 100%;
    max-width: 100%;
  }
`;

const Image = styled(Imgix)<{ src: string; imgixParams: Record<string, number | string> }>`
  display: block;
  margin: 0 auto;
  max-height: 100%;
  max-width: 100%;
`;

const StaffPick = styled.div`
  align-items: center;
  background: #fff;
  border-bottom-right-radius: ${({ theme }) => theme?.radius?.xs};
  color: ${({ theme }) => theme?.staffPickTagColor};
  display: flex;
  font-size: 12px;
  font-weight: 500;
  gap: 4px;
  left: 0;
  line-height: ${13 / 12};
  padding: 4px;
  position: absolute;
  text-transform: ${({ theme }) => theme?.textTransforms?.tags};
  top: 0;
`;

const Name = styled.div`
  color: #121516;
  font-size: 14px;
  font-weight: 600;
  line-height: ${16 / 14};
`;

const Brand = styled.div`
  color: #646d72;
  font: 12px/1 ${({ theme }) => theme?.secondaryFont};
  margin-top: 4px;
`;

type CollectionBadgeProps = {
  color: string | null;
  isExclusive: boolean;
  title: string;
};

const CollectionBadge = ({ color, isExclusive, title }: CollectionBadgeProps): JSX.Element => (
  <CollectionBadgeText $color={color} $isExclusive={isExclusive}>
    {title}
  </CollectionBadgeText>
);

const CollectionBadgeText = styled.div<{ $color: string | null; $isExclusive: boolean }>`
  align-items: center;
  color: ${({ $color }) => $color ?? '#646d72'};
  display: flex;
  font-size: 11px;
  gap: 4px;
  justify-content: var(--justify-content, flex-start);
  margin-top: 6px;
  width: 100%;

  ${({ $isExclusive }) =>
    !$isExclusive &&
    css`
      &::before {
        background: currentColor;
        content: '';
        height: 6px;
        margin: 0 3px;
        transform: rotate(45deg);
        width: 6px;
      }
    `}
`;

const Details = styled.div`
  min-height: 42px;
  margin-top: 16px;
`;

const Strain = styled.div`
  background: #e3e7e9;
  border-radius: 12px;
  color: #2c3236;
  display: inline-block;
  font: 500 12px/1 ${({ theme }) => theme?.secondaryFont};
  padding: 6px 8px;
`;

const Effects = styled.div`
  color: #646d72;
  font: 12px/1 ${({ theme }) => theme?.secondaryFont};
  margin-top: 6px;
`;

const Potency = styled.div<{ $isMissingStrain: boolean }>`
  color: #646d72;
  font: 12px/1 ${({ theme }) => theme?.secondaryFont};
  margin-top: 6px;

  ${({ $isMissingStrain }) =>
    $isMissingStrain &&
    css`
      padding-top: 24px;
    `}
`;

const Tags = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 12px;
  font-weight: 500;
  gap: 4px;
  line-height: ${13 / 12};
  margin-top: 16px;
  text-transform: ${({ theme }) => theme?.textTransforms?.tags};
`;

const Tag = styled.div`
  align-items: center;
  display: flex;
  gap: 4px;
  justify-content: var(--justify-content);
  width: 100%;
`;

const SpecialOffer = styled(Tag)`
  color: ${({ theme }) => theme.colors.green[40]};
`;

const Price = styled.div`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 4px;
  margin: 16px 0 8px;
  width: 100%;
`;

const PriceRow = styled.div`
  align-items: flex-end;
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
`;

const WeightText = styled.div`
  color: #646d72;
  font-size: 12px;
  line-height: 1;
  margin-top: 2px;
`;

const IconButton = styled.button<{ $buttonFlair?: MobileAppFlair }>`
  align-items: center;
  appearance: none;
  background: ${({ theme }) => theme.linkColor};
  border: none;
  border-radius: ${({ $buttonFlair }) => (isCircularButtonFlair($buttonFlair) ? '50%' : '0')};
  clip-path: ${({ $buttonFlair }) => getButtonClipPath($buttonFlair) || 'none'};
  color: #fff;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 36px;
  justify-content: center;
  width: 36px;

  svg {
    fill: currentColor;
    height: 14px;
    width: 14px;
  }
`;

const ActivePrice = styled.div`
  color: #2c3236;
  font-size: 20px;
  font-weight: 600;
  line-height: 1;
`;

const Weight = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: ${20 / 14};
`;

const DiscountedPrice = styled.div`
  align-items: center;
  display: flex;
  gap: 6px;
  justify-content: var(--justify-content);
`;

const OriginalPrice = styled.div`
  color: #485055;
  font-size: 14px;
  line-height: 1;
  text-decoration: line-through;
`;

const Discount = styled.div`
  background: ${({ theme }) => theme?.discountTagColor};
  border-radius: ${({ theme }) => theme?.radius?.tags};
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: -0.5px;
  line-height: 1;
  margin-left: 2px;
  padding: 2px 6px;
  white-space: nowrap;
`;

const tintedButtonStyles = css`
  /* Hex alpha 10% */
  background: ${({ theme }) => theme.linkColor}1A;
  color: ${({ theme }) => TinyColor.mix(theme.linkColor, '#000', 20).toString()};
`;

const solidButtonStyles = css`
  background: ${({ theme }) => theme.linkColor};
  color: #fff;
`;

const solidButtonHoverStyles = css`
  background: ${({ theme }) => TinyColor.mix(theme.linkColor, '#000', 20).toString()};
  color: #fff;
`;

const Button = styled.button`
  appearance: none;
  border: none;
  border-radius: ${({ theme }) => theme?.radius?.buttons};
  cursor: pointer;
  display: block;
  font-weight: bold;
  padding: 10px 12px;
  text-transform: ${({ theme }) => theme?.textTransforms?.buttons};
  width: 100%;

  ${({ theme }) =>
    theme.productCardButton === ProductCardButtonOption.tinted ? tintedButtonStyles : solidButtonStyles}

  &:hover {
    ${({ theme }) =>
      theme.productCardButton === ProductCardButtonOption.tinted ? solidButtonStyles : solidButtonHoverStyles}
  }
`;
